import { useEffect, useState, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import ListLoadError from '../../components/admin/ListLoadError';
import RoleEditor from './RoleEditor.jsx';
import RoleUsersPanel from './RoleUsersPanel.jsx';
import api from '../../services/api';
import styles from './RolesPage.module.css';

const ss = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 };
const ICON = {
  star: <svg width="24" height="24" viewBox="0 0 24 24" {...ss}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>,
  shield: <svg width="24" height="24" viewBox="0 0 24 24" {...ss}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  lock: <svg width="16" height="16" viewBox="0 0 24 24" {...ss}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>,
  lockSm: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>,
  edit: <svg width="14" height="14" viewBox="0 0 24 24" {...ss}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" {...ss}><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>,
  shieldLg: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
};

const EMPTY_ROLE_FORM = { name: '', description: '', color: '#E8732A' };

export default function RolesPage() {
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [rolesError, setRolesError] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState(EMPTY_ROLE_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const canEdit = hasPermission('roles.edit');

  const loadRoles = useCallback(async () => {
    try {
      const data = await api.get('/admin/roles');
      setRolesError(false);
      setRoles(data.roles || []);
      // Keep the selected role's summary (perm/user counts) fresh after edits.
      setSelectedRole((prev) => (prev ? (data.roles || []).find((r) => r.id === prev.id) || prev : prev));
    } catch { setRolesError(true); toast.error('Failed to load roles'); } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const handleCreateRole = async () => {
    if (!formData.name.trim()) { setNameError('Name is required'); return; }
    setNameError(''); setFormLoading(true);
    try {
      const data = await api.post('/admin/roles', formData);
      toast.success('Role created'); setShowCreateModal(false); setFormData(EMPTY_ROLE_FORM);
      await loadRoles();
      if (data.role) setSelectedRole(data.role);
    } catch (err) { toast.error(err.message || 'Failed to create role'); } finally { setFormLoading(false); }
  };

  const handleEditRole = async () => {
    if (!formData.name.trim()) { setNameError('Name is required'); return; }
    setNameError(''); setFormLoading(true);
    try {
      await api.patch(`/admin/roles/${showEditModal.id}`, formData);
      toast.success('Role updated'); setShowEditModal(false); await loadRoles();
    } catch (err) { toast.error(err.message || 'Failed to update role'); } finally { setFormLoading(false); }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await api.delete(`/admin/roles/${roleId}`);
      toast.success('Role deleted'); setShowDeleteConfirm(null);
      if (selectedRole?.id === roleId) setSelectedRole(null);
      loadRoles();
    } catch (err) { toast.error(err.message || 'Failed to delete role'); }
  };

  const openCreate = () => { setNameError(''); setFormData(EMPTY_ROLE_FORM); setShowCreateModal(true); };
  const openEdit = (role) => { setNameError(''); setFormData({ name: role.name, description: role.description || '', color: role.color || '#E8732A' }); setShowEditModal(role); };
  const setField = (key) => (e) => { if (key === 'name') setNameError(''); setFormData((p) => ({ ...p, [key]: e.target.value })); };

  const roleFormFields = (
    <div className={styles.form}>
      <div className={styles.formGroup}><label className={styles.label}>Name</label><Input value={formData.name} onChange={setField('name')} placeholder="e.g. Customer Support" />{nameError && <span className={styles.fieldError}>{nameError}</span>}</div>
      <div className={styles.formGroup}><label className={styles.label}>Description</label><textarea className={styles.textarea} value={formData.description} onChange={setField('description')} placeholder="Brief description of this role" /></div>
      <div className={styles.formGroup}><label className={styles.label}>Color</label><div className={styles.colorPicker}><input type="color" className={styles.colorInput} value={formData.color} onChange={setField('color')} /><span className={styles.colorValue}>{formData.color}</span></div></div>
    </div>
  );

  if (loading) return <div className={styles.loading}><LoadingSpinner /></div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div><h1>Roles &amp; Permissions</h1><p>Manage admin roles and configure access permissions</p></div>
        {canEdit && <Button onClick={openCreate}>+ Create Role</Button>}
      </div>

      <div className={styles.twoPanel}>
        <div className={styles.leftPanel}>
          {roles.map((role) => (
            <div key={role.id} className={`${styles.roleCard} ${selectedRole?.id === role.id ? styles.roleCardSelected : ''}`} onClick={() => setSelectedRole(role)}>
              <div className={styles.roleHeader}>
                <div className={styles.roleIcon} style={{ backgroundColor: role.color || '#8b5cf6' }}>{role.isSystem ? ICON.star : ICON.shield}</div>
                <div className={styles.roleActions}>
                  {role.isSystem ? (
                    <span className={styles.lockIcon} title="System role — locked">{ICON.lock}</span>
                  ) : canEdit && (
                    <>
                      <button className={styles.actionBtn} title="Edit role" onClick={(e) => { e.stopPropagation(); openEdit(role); }}>{ICON.edit}</button>
                      <button className={`${styles.actionBtn} ${styles.danger}`} title="Delete role" onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(role); }}>{ICON.trash}</button>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.roleInfo}>
                <h3 className={styles.roleName}>{role.name}</h3>
                {role.description && <p className={styles.roleDescription}>{role.description}</p>}
              </div>
              <div className={styles.roleStats}>
                <div className={styles.stat}><span className={styles.statValue}>{role.permissions?.length ?? 0}</span><span className={styles.statLabel}>Permissions</span></div>
                <div className={styles.stat}><span className={styles.statValue}>{role.userCount ?? String.fromCharCode(8212)}</span><span className={styles.statLabel}>Users</span></div>
              </div>
              <div className={styles.roleFooter}>
                {role.isSystem
                  ? <span className={styles.systemBadge}>{ICON.lockSm} Locked System Role</span>
                  : <span className={styles.permHint}>Click to configure</span>}
                <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: role.color || '#8b5cf6', display: 'inline-block' }} />
              </div>
            </div>
          ))}
          {roles.length === 0 && (rolesError
            ? <ListLoadError message="Could not load roles" onRetry={() => loadRoles()} />
            : <div className={styles.empty}><p>No roles found. Create one to get started.</p></div>)}
        </div>

        <div className={styles.rightPanel}>
          {!selectedRole ? (
            <div className={styles.emptyRight}>{ICON.shieldLg}<p>Select a role to configure permissions</p></div>
          ) : (
            <div className={styles.roleDetail}>
              <div className={styles.permEditorHeader}>
                <div>
                  <h2 className={styles.permEditorTitle}><span className={styles.roleColorDot} style={{ backgroundColor: selectedRole.color || '#8b5cf6' }} />{selectedRole.name}</h2>
                  {selectedRole.description && <p className={styles.permEditorSub}>{selectedRole.description}</p>}
                </div>
              </div>

              <RoleUsersPanel key={selectedRole.id} role={selectedRole} canEdit={canEdit} onChanged={loadRoles} />

              <RoleEditor role={selectedRole} canEdit={canEdit} onSaved={loadRoles} />
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create Role">
        {roleFormFields}
        <div className={styles.formActions}><Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button><Button loading={formLoading} onClick={handleCreateRole}>Create Role</Button></div>
      </Modal>

      <Modal isOpen={!!showEditModal} onClose={() => setShowEditModal(false)} title="Edit Role">
        {roleFormFields}
        <div className={styles.formActions}><Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button><Button loading={formLoading} onClick={handleEditRole}>Save Changes</Button></div>
      </Modal>

      <Modal isOpen={!!showDeleteConfirm} onClose={() => setShowDeleteConfirm(null)} title="Delete Role">
        <div className={styles.confirmDialog}>
          <p>Are you sure you want to delete <strong>{showDeleteConfirm?.name}</strong>?</p>
          {showDeleteConfirm && showDeleteConfirm.userCount !== 0 && <div className={styles.warningText}>{(showDeleteConfirm.userCount ?? null) === null ? String.fromCharCode(9888) + " The number of users assigned to this role could not be read. If any are assigned, they will lose their admin access." : `This role has ${showDeleteConfirm.userCount} assigned user(s). They will lose their admin access.`}</div>}
          <div className={styles.formActions}><Button variant="secondary" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button><Button variant="danger" onClick={() => handleDeleteRole(showDeleteConfirm.id)}>Delete Role</Button></div>
        </div>
      </Modal>
    </div>
  );
}
